// Client-side entry point for external integration operations.
//
// Org-owned integrations (kind='github',
// 'google_workspace', 'npm', etc.) live in the Symbols server's
// Mongo-backed `OrgIntegration` collection. Their secrets stay server-side
// (Vault / GSM) and never reach the browser — every op round-trips through
// the server's `/org-integrations/*` REST routes with the caller's SDK
// bearer token.
//
// Every op routes through the typed @symbo.ls/sdk IntegrationService (see
// `_viaSDK` below) — the SDK owns the base URL, the Mongo bearer token, and
// the route paths. Success returns the bare server payload
// (`{ ok, ... }` / `{ items: [...] }` / `{ kinds: [...] }`); failure returns
// `{ error: string, status? }` (the SDK throws, `_viaSDK` translates).
//
// The server re-checks org membership from the token: 401 on an
// unauthenticated caller, 403 on a non-member, 404 on a missing integration.
//
// Routes used here:
//   GET  /org-integrations?orgId=&scopeType=&scopeId=&kind=&includeParents=
//                                       — list org / scope integrations
//   GET  /org-integrations/kinds        — integration_kinds catalogue
//   POST /org-integrations              — upsert an integration instance
//   DEL  /org-integrations              — delete an instance (exact scope)
//   POST /org-integrations/assign-scope — move a row between scopes
//   POST /org-integrations/reorder      — persist drag-and-drop order
//   POST /org-integrations/call         — capability dispatcher (per-kind)
//   /marketplace/integrations/*         — paid-path entitlement + install
//
// The Mongo server returns canonical camelCase rows; consumers were written
// against the legacy snake_case shape, so rows/kinds are normalized at this
// boundary (see `_normalizeRow` / `_normalizeKind`).

import { slugify } from '../slugify.js'
import { getSDK } from '../sdk.js'

// SDK-only transport (workspace rule: never raw fetch from project code).
// Every op routes through the typed @symbo.ls/sdk IntegrationService methods,
// which own the base URL, the Mongo bearer token, and the
// /org-integrations|/marketplace routing — no hand-rolled fetch/URL/token here.
//
// `_viaSDK` bridges the two error conventions: the SDK THROWS on failure, but
// this module's callers expect a return-based `{ error, status? }`. On success
// the SDK's `_call` returns the bare server payload (`{ items }` / `{ kinds }` /
// `{ ok, ... }`) — identical to what the old fetch returned, so every
// downstream reader (_readList*, _fold, marketplace UI) is unchanged.
const _viaSDK = async (run) => {
  try {
    return await run(getSDK())
  } catch (err) {
    return { error: err?.message || 'request failed', status: err?.statusCode || err?.status }
  }
}

// The Mongo-backed Node server returns canonical camelCase rows; the
// Wire rows are snake_case. Consumers (loadIntegrationUnits / _fold,
// marketplace catalog, admin hub) were written against the snake_case shape,
// so normalize the Mongo rows at the SDK boundary instead of forking every
// downstream call site.
//
// `pick(snake, camel)` uses presence (`in`) rather than `??` so an explicit
// `null` value on the snake-case key (e.g. `scope_id: null` for org-scoped
// rows) is preserved instead of falling through to the camel-case sibling.
const _pick = (obj, snake, camel) => (snake in obj ? obj[snake] : obj[camel])

const _normalizeRow = (row) =>
  row && {
    ...row,
    display_name: _pick(row, 'display_name', 'displayName'),
    scope_type: _pick(row, 'scope_type', 'scopeType'),
    scope_id: _pick(row, 'scope_id', 'scopeId'),
    has_secret: _pick(row, 'has_secret', 'hasSecret'),
    updated_at: _pick(row, 'updated_at', 'updatedAt')
  }

const _normalizeKind = (kind) =>
  kind && {
    ...kind,
    auth_model: _pick(kind, 'auth_model', 'authModel'),
    default_scope: _pick(kind, 'default_scope', 'defaultScope'),
    allows_multiple: _pick(kind, 'allows_multiple', 'allowsMultiple'),
    config_schema: _pick(kind, 'config_schema', 'configSchema'),
    secret_label: _pick(kind, 'secret_label', 'secretLabel'),
    secret_hint: _pick(kind, 'secret_hint', 'secretHint'),
    sort_order: _pick(kind, 'sort_order', 'sortOrder'),
    // CU-INT §180 — marketplace paid path. Surfaces pricing so the UI can
    // render "Free" vs "Paid" badges + use kind.pricing.stripePriceId when
    // wiring its own Checkout buttons. Null/absent on free kinds.
    pricing: kind.pricing ?? null
  }

// Mongo list endpoints return `{ items: [...] }`; legacy paths
// returned `{ integrations: [...] }`. Read either shape so the SDK is
// resilient to either side of the cutover.
const _readListItems = (resp) => resp?.items || resp?.integrations || []

const _readListIntegrations = (resp) => _readListItems(resp).map(_normalizeRow)

const _readListKinds = (resp) => (resp?.kinds || []).map(_normalizeKind)

// ---------- named helpers ----------

// List every integration for the org (any kind). Useful for the admin
// hub page to show counts per kind.
export const listOrgIntegrations = (orgId) =>
  _viaSDK(async (sdk) => {
    const r = await sdk.listOrgIntegrations({ orgId })
    return { ok: true, result: _readListIntegrations(r) }
  })

// ---------- admin ops (integration config CRUD) ----------
//
// These POST / DELETE against the `/org-integrations` routes. The server
// validates the SDK bearer token, checks admin/owner membership on the
// target org, then performs the secret-store + OrgIntegration upsert.
// Membership is enforced server-side so it can't be skipped by tampering
// with the request.

// Upsert an integration instance. Args:
//   orgId, kind, slug?, scopeType?='org', scopeId?=null,
//   displayName?, config?, secret?, enabled?
// scopeType='org' → scopeId must be null (server enforces).
// scopeType='workspace'|'project' → scopeId required.
export const saveIntegration = (args) => _viaSDK((sdk) => sdk.upsertOrgIntegration(args))

// Delete an integration instance. Must match the row's scope exactly
// (no inheritance on delete). Args: orgId, kind, slug?, scopeType?, scopeId?
export const deleteIntegration = (args) => _viaSDK((sdk) => sdk.deleteOrgIntegration(args))

// Marketplace-facing wrapper over saveIntegration. Generates a slug
// from displayName and enforces the scope-null-for-org invariant.
export const addIntegrationFromMarketplace = (args) => {
  if (!args?.kind) return Promise.resolve({ ok: false, body: { error: 'kind required' } })
  const scopeType = args.scopeType || 'org'
  return saveIntegration({
    orgId: args.orgId,
    kind: args.kind,
    slug: slugify(args.displayName || args.config?.display_name || args.kind),
    displayName: args.displayName || args.kind,
    config: args.config || {},
    secret: args.secret,
    scopeType,
    scopeId: scopeType === 'org' ? null : args.scopeId || null,
    enabled: true
  })
}

// Move an existing row from one scope to another. Args:
//   orgId, kind, slug?,
//   fromScopeType?, fromScopeId?,   -- where the row lives today
//   toScopeType, toScopeId?         -- where it should live
export const assignIntegrationScope = (args) =>
  _viaSDK((sdk) => sdk.assignOrgIntegrationScope(args))

// §I12 — Persist drag-and-drop instance reorder.
// `slugs` is the new ordered array for the given (orgId, kind, scopeType, scopeId).
// Each slug at index i gets position=i in org_integrations / OrgIntegration.position.
// Args: orgId, kind, slugs, scopeType?='org', scopeId?=null
export const reorderIntegrations = (args) => _viaSDK((sdk) => sdk.reorderOrgIntegrations(args))

// Read the integration_kinds catalogue + the current org's configured
// integrations. Small helper that batches the two in one round trip so the
// admin hub renders without a waterfall.
export const loadIntegrationCatalogue = async (orgId) => {
  const [kindsRes, integrationsRes] = await Promise.all([
    _viaSDK((sdk) => sdk.listOrgIntegrationKinds()),
    orgId ? _viaSDK((sdk) => sdk.listOrgIntegrations({ orgId })) : Promise.resolve({ items: [] })
  ])
  return {
    kinds: _readListKinds(kindsRes),
    integrations: _readListIntegrations(integrationsRes)
  }
}

// ---------- scope-aware unified reader ----------
//
// The "integration units" shape. One entry per configured kind at the
// requested scope (with parent-scope inheritance by default). Built on the
// `/org-integrations` list route (scopeType / scopeId / includeParents
// params) which handles the inheritance query server-side.
//
// Shape returned:
//   [{
//     kind, label, category, icon, description,
//     auth_model, default_scope, capabilities, allows_multiple,
//     instances: [
//       { slug, display_name, scope_type, scope_id, config, enabled, has_secret, updated_at }
//     ],
//     count
//   }, …]
//
// Scope descriptor:
//   { type: 'org' | 'workspace' | 'project', orgId, id?, includeParents? }
//
// `id` is required for workspace/project scope. `includeParents` defaults
// to true — a project view merges project + workspace + org rows so
// child contexts inherit org-wide infra (github, google_workspace, etc).

const _fold = (kinds, integrations) => {
  const byKind = {}
  for (const k of kinds || []) {
    byKind[k.kind] = {
      kind: k.kind,
      label: k.label,
      category: k.category,
      icon: k.icon,
      description: k.description,
      auth_model: k.auth_model,
      default_scope: k.default_scope,
      capabilities: k.capabilities || [],
      allows_multiple: k.allows_multiple,
      instances: [],
      count: 0
    }
  }
  for (const i of integrations || []) {
    const unit = byKind[i.kind]
    if (!unit) continue
    unit.instances.push({
      slug: i.slug,
      display_name: i.display_name,
      scope_type: i.scope_type,
      scope_id: i.scope_id,
      config: i.config,
      enabled: i.enabled,
      has_secret: i.has_secret,
      updated_at: i.updated_at
    })
    unit.count += 1
  }
  return Object.values(byKind).sort((a, b) => a.category.localeCompare(b.category))
}

export const loadIntegrationUnits = async (scope = {}) => {
  // `type` null is no type — the org default (a destructuring default fires
  // for undefined only; null reached the SDK as scopeType="null" → 400).
  const { orgId, id = null, includeParents = true } = scope
  const type = scope.type ?? 'org'
  if (!orgId) return []
  if ((type === 'workspace' || type === 'project') && !id) {
    throw new Error(`loadIntegrationUnits: scope.id required for type="${type}"`)
  }
  const [kindsRes, rowsRes] = await Promise.all([
    _viaSDK((sdk) => sdk.listOrgIntegrationKinds()),
    _viaSDK((sdk) =>
      sdk.listOrgIntegrations({ orgId, scopeType: type, scopeId: id || undefined, includeParents })
    )
  ])
  return _fold(_readListKinds(kindsRes), _readListIntegrations(rowsRes))
}

// Installed rows ONLY, scope-aware — the fold-free half of
// loadIntegrationUnits, for consumers that read the catalogue separately
// (InstallableRail fetches kinds + instances as two parallel loads). Same
// scope contract as loadIntegrationUnits: `type` 'org' takes no id;
// 'workspace'/'project' require one.
export const listScopedIntegrations = async (scope = {}) => {
  // `type` null is no type — the org default (a destructuring default fires
  // for undefined only; null reached the SDK as scopeType="null" → 400).
  const { orgId, id = null, includeParents = true } = scope
  const type = scope.type ?? 'org'
  if (!orgId) return []
  if ((type === 'workspace' || type === 'project') && !id) {
    throw new Error(`listScopedIntegrations: scope.id required for type="${type}"`)
  }
  const rowsRes = await _viaSDK((sdk) =>
    sdk.listOrgIntegrations({ orgId, scopeType: type, scopeId: id || undefined, includeParents })
  )
  return _readListIntegrations(rowsRes)
}

// Convenience wrappers — pick whichever is most natural at the call site.
export const loadOrgIntegrations = (orgId, opts = {}) =>
  loadIntegrationUnits({ type: 'org', orgId, ...opts })

export const loadWorkspaceIntegrations = (orgId, workspaceId, opts = {}) =>
  loadIntegrationUnits({ type: 'workspace', orgId, id: workspaceId, ...opts })

export const loadProjectIntegrations = (orgId, projectId, opts = {}) =>
  loadIntegrationUnits({ type: 'project', orgId, id: projectId, ...opts })

// Catalogue-only read for UI that renders the "what could you connect?"
// list without needing an org context (onboarding, marketing). Reads the
// integration_kinds catalogue from the server's `/org-integrations/kinds`
// route and normalizes each row to the snake_case consumer shape.
export const listIntegrationKinds = async () => {
  const res = await _viaSDK((sdk) => sdk.listOrgIntegrationKinds())
  return _readListKinds(res)
}

// Capability discovery — return all kinds that declare a capability.
// Useful when the canvas asks "which integrations can send webhooks?"
export const findIntegrationsByCapability = async (capability, scope) => {
  const units = await loadIntegrationUnits(scope)
  return units.filter((u) => (u.capabilities || []).includes(capability))
}

// ---------- capability execution ----------
//
// Generic verb dispatcher. The `/org-integrations/call` route looks up the
// integration row by (orgId, scope, kind, slug), verifies the caller's org
// membership, and routes to a per-kind handler. The capability string
// ('webhook.trigger', 'http.request', 'metric.query', …) tells the handler
// *what* to do; `args` is opaque payload.
//
// Used for n8n, custom_http, google_analytics, and future kinds.
//
// ref shape: { orgId, kind, slug?='default', scopeType?='org', scopeId?=null, workspaceId? }
// `workspaceId` is REQUIRED for paid kinds (the server's entitlement gate 400s
// `workspace_required` without it) and is also recorded on the audit log.
export const callCapability = async (ref, capability, args = {}) =>
  _viaSDK((sdk) =>
    sdk.callOrgIntegrationCapability({
      orgId: ref.orgId,
      kind: ref.kind,
      slug: ref.slug || 'default',
      scopeType: ref.scopeType || 'org',
      scopeId: ref.scopeId ?? null,
      workspaceId: ref.workspaceId,
      capability,
      args
    })
  )

// ---------- marketplace integration paid path (CU-INT §180) ------------
//
// Wrappers over /marketplace/integrations/*. Each helper returns the server
// JSON verbatim ({ ok, ... } on success, { error, status } on failure) so
// callers can branch on `result.error` uniformly.

// List active marketplace entitlements for a workspace.
// Optional `status` filters (defaults to 'active,trialing').
// Returns { items: [...] } on success.
export const listMarketplaceEntitlements = async ({ workspaceId, status } = {}) => {
  if (!workspaceId) return { error: 'workspaceId required' }
  return _viaSDK((sdk) => sdk.listMarketplaceEntitlements(workspaceId, { status }))
}

// Check whether the workspace has an active entitlement for a given kind.
// Returns { active: boolean, entitlement: {...}|null }.
export const checkMarketplaceEntitlement = async ({ workspaceId, kind } = {}) => {
  if (!workspaceId || !kind) return { error: 'workspaceId + kind required' }
  return _viaSDK((sdk) => sdk.checkMarketplaceEntitlement({ workspaceId, kind }))
}

// Install an integration kind for a workspace. Behaviour depends on kind.pricing:
//   - free:  creates OrgIntegration row immediately → { installed: 'free' }
//   - paid w/ existing entitlement: same as free → { installed: 'paid_existing_entitlement' }
//   - paid w/o entitlement: creates Stripe Checkout Session →
//     { installed: 'checkout_required', checkoutUrl, sessionId }
//
// Args: { orgId, workspaceId, kind, slug?, scopeType?='workspace', scopeId?,
//         displayName?, config?, secret?, successUrl?, cancelUrl? }
export const installMarketplaceIntegration = async (args = {}) => {
  if (!args.orgId || !args.workspaceId || !args.kind) {
    return { error: 'orgId, workspaceId, kind required' }
  }
  return _viaSDK((sdk) => sdk.installMarketplaceIntegration(args))
}

// Uninstall a workspace's integration kind. Cancels the Stripe subscription
// (if paid) and deletes the OrgIntegration row + GSM secret.
// Args: { orgId, workspaceId, kind, slug?, scopeType?, scopeId?, cancelSubscription? }
export const uninstallMarketplaceIntegration = async (args = {}) => {
  if (!args.orgId || !args.workspaceId || !args.kind) {
    return { error: 'orgId, workspaceId, kind required' }
  }
  return _viaSDK((sdk) => sdk.uninstallMarketplaceIntegration(args))
}

// Internal exports for tests — kept lightweight so the public surface
// stays focused. Renaming any of these is a breaking change.
// @testOnly — exported for integrations/index.test.js; no live code imports or calls it.
export const _internals = {
  normalizeRow: _normalizeRow,
  normalizeKind: _normalizeKind,
  readListItems: _readListItems,
  readListIntegrations: _readListIntegrations,
  readListKinds: _readListKinds
}
